import React, { useState, forwardRef, useImperativeHandle } from "react";
import styles from "./BusinessStep.module.css";
import { trackEvent } from "../../lib/analytics";

const StepNameZip = forwardRef(({ data, updateFields }, ref) => {
  const [name, setName] = useState(data.placeDetails?.businessName || data.displayBusinessName || "");
  const [zip, setZip] = useState(data.zipCode || "");
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = "Your Business Name is required.";
    }
    if (!zip.trim()) {
      newErrors.zip = "Zip / Postal Code is required.";
    } else if (zip.trim().length < 4) {
      newErrors.zip = "Please enter a valid zip / postal code.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const isValid = validate();
      if (isValid) {
        // Update the form data with manual entries
        const cleanName = name.trim();
        const cleanZip = zip.trim();
        
        const placeDetails = {
          ...(data.placeDetails || {}),
          businessName: cleanName,
          postal_code: cleanZip,
        };

        updateFields({
          displayBusinessName: cleanName,
          zipCode: cleanZip,
          placeDetails,
          noGoogleListing: true,
          isGMBSelected: false,
        });

        trackEvent("businessname_zipcode_entered_confirmed", {
          business_name: cleanName,
          zip: cleanZip,
        });
      }
      return isValid;
    }
  }));

  const handleNameChange = (e) => {
    // Strip numeric digits and emojis
    const value = e.target.value
      .replace(/[0-9]/g, "")
      .replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/g, "");
    setName(value);
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: null }));
    }
  };

  const handleZipChange = (e) => {
    const value = e.target.value.trim();
    setZip(value);
    if (errors.zip) {
      setErrors((prev) => ({ ...prev, zip: null }));
    }
  };

  return (
    <div className={styles.nameZipContainer}>
      <div className={styles.titleBar}>
        <h4 className={styles.lightTitle}>Enter your</h4>
        <h5 className={styles.boldTitle}>business details</h5>
        <p className={styles.subtitle}>
          Provide your business name and zip code. We'll use this information to answer calls correctly
        </p>
      </div>

      <div className={styles.manualEntryForm}>
        {/* Business Name Field */}
        <div className={`${styles.cardInputContainer} ${errors.name ? styles.cardInputError : ""}`}>
          <label className={styles.cardInputLabel}>
            Your Business Name <span className={styles.req}>*</span>
          </label>
          <input
            type="text"
            placeholder="Your Business Name"
            value={name}
            onChange={handleNameChange}
            className={styles.cardInputControl}
          />
          {errors.name && <p className={styles.errorText}>{errors.name}</p>}
        </div>

        {/* Zip / Postal Code Field */}
        <div className={`${styles.cardInputContainer} ${errors.zip ? styles.cardInputError : ""}`}>
          <label className={styles.cardInputLabel}>
            Zip / Postal Code <span className={styles.req}>*</span>
          </label>
          <input
            type="text"
            placeholder="10019"
            value={zip}
            onChange={handleZipChange}
            className={styles.cardInputControl}
          />
          {errors.zip && <p className={styles.errorText}>{errors.zip}</p>}
        </div>

        {/* Info Alert Box */}
        <div className={styles.zipAlertBox}>
          <span className={styles.infoAlertIcon}>!</span>
          <p className={styles.infoAlertText}>
            Type your zip / postal code to find your business location.
          </p>
        </div>
      </div>
    </div>
  );
});

export default StepNameZip;
